import { Box } from "@mui/material";
import { type RunDetailOut } from "@platform/api/golden";
import { Typography } from "@platform/design-system";

import { RfpLink } from "../shared/RfpLink";
import { formatTimestamp } from "../shared/values";

import { estimatedCost } from "./runCost";
import { isRunGoing } from "./runQueries";
import { type RunStream } from "./useRunStream";

import type { ReactNode } from "react";

export const Fact = ({
  term,
  children,
}: {
  readonly term: string;
  readonly children: ReactNode;
}): ReactNode => (
  <>
    <Box component="dt">
      <Typography variant="body-medium" color="textSecondary" component="span">
        {term}
      </Typography>
    </Box>
    <Box component="dd" sx={{ m: 0 }}>
      <Typography variant="body-medium" component="span">
        {children}
      </Typography>
    </Box>
  </>
);

export const factsSx = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: 3,
  rowGap: 1,
  m: 0,
} as const;

const costLabel = (model: string, stream: RunStream): string => {
  if (stream.connection === "unavailable")
    return "unavailable: the run's steps could not be loaded";
  if (stream.connection !== "ended") return "counting the run's steps";
  const cost = estimatedCost(model, stream.steps);
  if (cost === undefined) return `no shelf price for ${model}`;
  return stream.droppedEvents
    ? `at least $${cost.toFixed(2)}: some of the run's steps could not be read`
    : `$${cost.toFixed(2)}`;
};

const versionFacts = (detail: RunDetailOut, stream: RunStream): ReactNode => {
  const version = detail.rumi_version;
  if (version) {
    return (
      <>
        <Fact term="RUMI model">
          {version.model} {version.model_version ?? ""}
        </Fact>
        <Fact term="Runner">
          {version.runner_version ?? version.runner_image ?? "—"}
        </Fact>
        <Fact term="Estimated cost">{costLabel(version.model, stream)}</Fact>
      </>
    );
  }
  const isUnfinished = isRunGoing(detail);
  return (
    <Fact term="RUMI version">
      {isUnfinished
        ? "recorded when the run ends"
        : "not recorded (a local run, or the runner never reported)"}
    </Fact>
  );
};

/** What a run filled and with which RUMI, what it cost, and when it ran; shown on the run page and its case's page. */
export const RunFacts = ({
  detail,
  stream,
}: {
  readonly detail: RunDetailOut;
  readonly stream: RunStream;
}): ReactNode => (
  <>
    <Fact term="RFP RUMI filled">
      <RfpLink id={detail.run.replay_rfp_id} url={detail.run.replay_rfp_url} />
    </Fact>
    <Fact term="Replay">{detail.run.replay_kind}</Fact>
    {versionFacts(detail, stream)}
    <Fact term="Started">{formatTimestamp(detail.run.created_at)}</Fact>
    <Fact term="Finished">{formatTimestamp(detail.run.finished_at)}</Fact>
  </>
);
